import React, { memo, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { getDataOfType } from '../../lib/utils/get-data-of-type'
import AppLoader from '../components/app-loader'
import { Routes, Route, Navigate } from 'react-router-dom'
import { routes } from './routes-config'
import type { RouteConfig } from './routes-config'
import Error404Page from '../pages/error-404-not-found'
import ModuleInactivePage from '../pages/module-inactive'
import { PrivateRoute } from '../utilits'
import { B2BLayoutOutlet } from '../layouts/b2b'
import { PollGuard } from '../components/poll-guard'
import { resolveStartPage } from '../pages_v2/home/owner-access'

export const DesktopRouter = memo(() => {
  const select = state => ({
    page: state.page,
    session: state.session,
  })
  const { page, session } = useSelector(select)
  const startPage = useMemo(() => {
    // /home — только владельцу кабинета (бэкенд тоже не отдаёт её другим как start_page)
    return resolveStartPage(
      getDataOfType(session, 'sessionData.start_page', String, '/orders'),
      Boolean(session?.sessionData?.isTenantOwner),
    )
  }, [session])

  const activeModules: string[] = useMemo(() => {
    return session?.sessionData?.activeModules || []
  }, [session])

  const {
    publicRoutes,
    noLayoutPrivateRoutes,
    unrestrictedPrivateRoutes,
    restrictedRouteGroups,
    moduleBlockedRoutes,
  } = useMemo(() => {
    const publicRoutesAccumulator: RouteConfig[] = []
    const noLayoutAccumulator: RouteConfig[] = []
    const unrestrictedAccumulator: RouteConfig[] = []
    const restrictedMap = new Map<string, { allowedPermissions: string[]; items: RouteConfig[] }>()
    const blockedAccumulator: RouteConfig[] = []

    routes.forEach(route => {
      if (!route.private) {
        publicRoutesAccumulator.push(route)
        return
      }

      if (route.noLayout) {
        noLayoutAccumulator.push(route)
        return
      }

      // Module enforcement: blocked routes render ModuleInactivePage
      if (route.requiredModule && !activeModules.includes(route.requiredModule)) {
        blockedAccumulator.push(route)
        return
      }

      const hasPerms = Boolean(route.allowedPermissions && route.allowedPermissions.length > 0)
      if (!hasPerms) {
        unrestrictedAccumulator.push(route)
        return
      }

      const normalizedPerms = [...route.allowedPermissions!].sort()
      const key = `p:${normalizedPerms.join(',')}`
      const current = restrictedMap.get(key)

      if (current) {
        current.items.push(route)
        return
      }

      restrictedMap.set(key, {
        allowedPermissions: normalizedPerms,
        items: [route],
      })
    })

    return {
      publicRoutes: publicRoutesAccumulator,
      noLayoutPrivateRoutes: noLayoutAccumulator,
      unrestrictedPrivateRoutes: unrestrictedAccumulator,
      restrictedRouteGroups: Array.from(restrictedMap.values()),
      moduleBlockedRoutes: blockedAccumulator,
    }
  }, [activeModules])

  return (
    <>
      {page.isLoading && <AppLoader />}
      {!page.isLoading && (
        <Routes>
          {/* Корневой роут */}
          <Route
            path="/"
            element={
              session && session.sessionData && session.sessionData.user ? (
                <Navigate to={startPage} replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* Public routes */}
          {publicRoutes.map(r => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
          {/* Private routes without layout (e.g. onboarding) */}
          <Route element={<PrivateRoute session={session} />}>
            {noLayoutPrivateRoutes.map(route => (
              <Route key={route.path} path={route.path} element={route.element} />
            ))}
          </Route>
          {/* Private routes */}
          <Route element={<PrivateRoute session={session} />}>
            <Route
              element={
                <PollGuard>
                  <B2BLayoutOutlet />
                </PollGuard>
              }
            >
              {unrestrictedPrivateRoutes.map(route => (
                <Route key={route.path} path={route.path} element={route.element} />
              ))}
              {restrictedRouteGroups.map(group => (
                <Route
                  key={group.allowedPermissions.join(',')}
                  element={
                    <PrivateRoute session={session} allowedPermissions={group.allowedPermissions} />
                  }
                >
                  {group.items.map(route => (
                    <Route key={route.path} path={route.path} element={route.element} />
                  ))}
                </Route>
              ))}
              {moduleBlockedRoutes.map(route => (
                <Route key={route.path} path={route.path} element={<ModuleInactivePage />} />
              ))}
            </Route>
          </Route>
          {/* 404 */}
          <Route path="*" element={<Error404Page />} />
        </Routes>
      )}
    </>
  )
})

DesktopRouter.displayName = 'DesktopRouter'
